import React from 'react'
import { useNavigation } from '../contexts/NavigationContext'

interface LandingPageProps {
  onGetStarted: () => void
}

export function LandingPage({ onGetStarted }: LandingPageProps): React.ReactElement {
  const { setCurrentPage, openModal } = useNavigation()

  const handleFooterLink = (target: string) => {
    console.log('Footer link clicked:', target)
    setCurrentPage(target as any)
  }

  const handleWatchDemo = () => {
    openModal('about' as any)
  }
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-blue-50">
      {/* Sticky Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b-4 border-purple-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-lg">
              👶
            </div>
            <div>
              <h1 className="text-2xl font-black text-purple-700">BabyKingdom</h1>
              <p className="text-xs text-pink-600 font-bold">Magic & Fun for Kids</p>
            </div>
          </div>
          <button
            onClick={onGetStarted}
            className="px-6 py-2 bg-gradient-to-r from-pink-400 to-purple-400 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition"
          >
            Start Now 🚀
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="px-8 py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <div className="inline-block px-4 py-1 bg-pink-200 rounded-full text-pink-700 font-bold text-sm">
              ✨ Welcome to Magic
            </div>
            <h1 className="text-6xl lg:text-7xl font-black leading-tight text-purple-700">
              Your Child's
              <br />
              <span className="text-pink-500">Magical</span>
              <br />
              Kingdom
            </h1>
            <p className="text-lg text-gray-700 font-semibold max-w-lg leading-relaxed">
              A colorful world where babies grow, play, and have endless fun. Join thousands of happy families today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={onGetStarted}
                className="px-8 py-4 bg-gradient-to-r from-pink-400 to-purple-400 text-white font-black text-lg rounded-2xl shadow-lg hover:shadow-xl transition transform hover:scale-105"
              >
                🚀 Get Started
              </button>
              <button
                onClick={handleWatchDemo}
                className="px-8 py-4 border-4 border-purple-300 text-purple-700 font-black text-lg rounded-2xl hover:bg-purple-50 transition"
              >
                📺 Watch Demo
              </button>
            </div>
          </div>

          {/* Right Illustration */}
          <div className="flex justify-center">
            <svg className="w-full max-w-md drop-shadow-2xl" viewBox="0 0 400 500" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Character: Baby with crown */}
              <circle cx="200" cy="120" r="70" fill="#FDBCB4" stroke="#F8A89E" strokeWidth="3" />
              <circle cx="180" cy="110" r="8" fill="#2D3748" />
              <circle cx="220" cy="110" r="8" fill="#2D3748" />
              <circle cx="182" cy="108" r="3" fill="white" />
              <circle cx="222" cy="108" r="3" fill="white" />
              <path d="M 180 130 Q 200 145 220 130" stroke="#F8A89E" strokeWidth="3" fill="none" strokeLinecap="round" />
              
              <rect x="150" y="190" width="100" height="100" rx="20" fill="#F0B4D8" stroke="#E895C2" strokeWidth="3" />
              <rect x="110" y="210" width="40" height="70" rx="20" fill="#FDBCB4" stroke="#F8A89E" strokeWidth="3" />
              <circle cx="115" cy="285" r="15" fill="#FDBCB4" stroke="#F8A89E" strokeWidth="2" />
              <rect x="250" y="210" width="40" height="70" rx="20" fill="#FDBCB4" stroke="#F8A89E" strokeWidth="3" />
              <circle cx="285" cy="285" r="15" fill="#FDBCB4" stroke="#F8A89E" strokeWidth="2" />
              
              <rect x="160" y="290" width="35" height="80" rx="17" fill="#FFD9B3" stroke="#FFB347" strokeWidth="3" />
              <circle cx="177" cy="375" r="18" fill="#FFD9B3" stroke="#FFB347" strokeWidth="2" />
              <rect x="205" y="290" width="35" height="80" rx="17" fill="#FFD9B3" stroke="#FFB347" strokeWidth="3" />
              <circle cx="222" cy="375" r="18" fill="#FFD9B3" stroke="#FFB347" strokeWidth="2" />
              
              <circle cx="185" cy="230" r="6" fill="#E895C2" />
              <circle cx="215" cy="230" r="6" fill="#E895C2" />
              <circle cx="200" cy="270" r="6" fill="#E895C2" />
              
              {/* Crown */}
              <g transform="translate(200, 50)">
                <rect x="-50" y="0" width="100" height="20" rx="5" fill="#FFE4B5" stroke="#FFB347" strokeWidth="2" />
                <polygon points="-40,-15 -30,0 -20,-15 -10,0 0,-15 10,0 20,-15 30,0 40,-15" fill="#FFE4B5" stroke="#FFB347" strokeWidth="2" />
                <circle cx="-25" cy="-15" r="6" fill="#FF69B4" />
                <circle cx="0" cy="-18" r="7" fill="#FF69B4" />
                <circle cx="25" cy="-15" r="6" fill="#FF69B4" />
              </g>
              
              {/* Stars */}
              <g opacity="0.8">
                <path d="M 80 60 L 85 75 L 100 75 L 90 85 L 95 100 L 80 90 L 65 100 L 70 85 L 60 75 L 75 75 Z" fill="#FFE4B5" />
                <path d="M 320 140 L 325 155 L 340 155 L 330 165 L 335 180 L 320 170 L 305 180 L 310 165 L 300 155 L 315 155 Z" fill="#FFE4B5" />
              </g>
            </svg>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="h-1 bg-gradient-to-r from-purple-200 via-pink-200 to-blue-200"></div>

      {/* Features Section */}
      <section className="px-8 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-purple-700 mb-2">
              Why Kids Love It 💕
            </h2>
            <p className="text-xl text-gray-600 font-bold">
              Everything for happy play and growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { emoji: '🎮', title: 'Fun Quests', desc: 'Magical adventures' },
              { emoji: '📊', title: 'Track Growth', desc: 'See progress daily' },
              { emoji: '🎁', title: 'Collect Items', desc: 'Gather treasures' },
              { emoji: '⭐', title: 'Win Badges', desc: 'Achievements unlock' },
              { emoji: '🎉', title: 'Events', desc: 'Seasonal fun' },
              { emoji: '👨‍👩‍👧‍👦', title: 'Family Play', desc: 'Together time' },
            ].map((item, idx) => (
              <div key={idx} className="p-8 bg-gradient-to-br from-purple-100 to-pink-50 rounded-3xl border-4 border-purple-200 shadow-lg hover:shadow-xl transition transform hover:-translate-y-2">
                <div className="text-6xl mb-3">{item.emoji}</div>
                <h3 className="text-2xl font-black text-purple-700 mb-1">{item.title}</h3>
                <p className="text-gray-700 font-semibold">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="px-8 py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-10 bg-gradient-to-br from-purple-200 to-purple-100 rounded-3xl border-4 border-purple-300 shadow-lg">
            <div className="text-7xl font-black text-purple-700 mb-3">10K+</div>
            <p className="text-2xl font-black text-purple-700">Happy Families</p>
            <p className="text-purple-600 font-bold">Already playing</p>
          </div>
          <div className="text-center p-10 bg-gradient-to-br from-pink-200 to-pink-100 rounded-3xl border-4 border-pink-300 shadow-lg">
            <div className="text-7xl font-black text-pink-700 mb-3">50+</div>
            <p className="text-2xl font-black text-pink-700">Quests & Events</p>
            <p className="text-pink-600 font-bold">To explore</p>
          </div>
          <div className="text-center p-10 bg-gradient-to-br from-blue-200 to-blue-100 rounded-3xl border-4 border-blue-300 shadow-lg">
            <div className="text-7xl font-black text-blue-700 mb-3">100%</div>
            <p className="text-2xl font-black text-blue-700">Safe & Secure</p>
            <p className="text-blue-600 font-bold">Parent approved</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-8 py-20 bg-gradient-to-r from-purple-100 via-pink-100 to-blue-100">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-6xl font-black text-purple-700">
            Ready to Play? 🎪
          </h2>
          <p className="text-2xl text-gray-700 font-bold">
            Start your child's magical adventure today. It's free and safe!
          </p>
          <button
            onClick={onGetStarted}
            className="px-12 py-5 bg-gradient-to-r from-pink-400 to-purple-400 text-white font-black text-2xl rounded-2xl shadow-xl hover:shadow-2xl transition transform hover:scale-110"
          >
            🎉 Create Account
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-8 py-12 bg-white border-t-4 border-purple-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-lg font-black text-purple-700 mb-3">Product</h4>
              <ul className="space-y-1">
                <li><button onClick={() => handleFooterLink('features')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Features</button></li>
                <li><button onClick={() => handleFooterLink('pricing')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Pricing</button></li>
                <li><button onClick={() => handleFooterLink('security')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Security</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-black text-purple-700 mb-3">Company</h4>
              <ul className="space-y-1">
                <li><button onClick={() => handleFooterLink('about')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">About</button></li>
                <li><button onClick={() => handleFooterLink('blog')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Blog</button></li>
                <li><button onClick={() => handleFooterLink('careers')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Careers</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-black text-purple-700 mb-3">Support</h4>
              <ul className="space-y-1">
                <li><button onClick={() => handleFooterLink('help')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Help</button></li>
                <li><button onClick={() => handleFooterLink('contact')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Contact</button></li>
                <li><button onClick={() => handleFooterLink('parentsGuide')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Parents Guide</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-black text-purple-700 mb-3">Legal</h4>
              <ul className="space-y-1">
                <li><button onClick={() => handleFooterLink('privacy')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Privacy</button></li>
                <li><button onClick={() => handleFooterLink('terms')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Terms</button></li>
                <li><button onClick={() => handleFooterLink('cookies')} className="text-gray-700 hover:text-pink-600 font-bold cursor-pointer">Cookies</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t-2 border-purple-200 pt-6 text-center">
            <p className="text-purple-700 font-black">
              © 2026 BabyKingdom ✨ | Where every baby becomes a hero!
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
